import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useRef,
} from 'react';

const AuthContext = createContext();

const INACTIVITY_TIMEOUT = 10 * 60 * 1000; // 10 minutes

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);
  const inactivityTimerRef = useRef(null);

  const clearInactivityTimer = useCallback(() => {
    if (inactivityTimerRef.current) {
      clearTimeout(inactivityTimerRef.current);
      inactivityTimerRef.current = null;
    }
  }, []);

  const logout = useCallback(() => {
    clearInactivityTimer();
    setUser(null);
    setToken(null);
    localStorage.removeItem('token');
    localStorage.removeItem('user');
  }, [clearInactivityTimer]);

  const startInactivityTimer = useCallback(() => {
    clearInactivityTimer();
    inactivityTimerRef.current = setTimeout(() => {
      logout();
    }, INACTIVITY_TIMEOUT);
  }, [clearInactivityTimer, logout]);

  useEffect(() => {
    const storedUser = localStorage.getItem('user');
    if (storedUser && token) {
      setUser(JSON.parse(storedUser));
      startInactivityTimer();
    } else {
      clearInactivityTimer();
    }
    setLoading(false);
  }, [token, startInactivityTimer, clearInactivityTimer]);

  useEffect(() => {
    if (!token) {
      clearInactivityTimer();
      return undefined;
    }

    const events = ['mousemove', 'keydown', 'mousedown', 'touchstart', 'scroll'];
    const handleActivity = () => {
      startInactivityTimer();
    };

    events.forEach((event) => window.addEventListener(event, handleActivity));
    startInactivityTimer();

    return () => {
      events.forEach((event) => window.removeEventListener(event, handleActivity));
      clearInactivityTimer();
    };
  }, [token, startInactivityTimer, clearInactivityTimer]);

  const login = useCallback(
    (userData, authToken) => {
      setUser(userData);
      setToken(authToken);
      localStorage.setItem('token', authToken);
      localStorage.setItem('user', JSON.stringify(userData));
      startInactivityTimer();
    },
    [startInactivityTimer],
  );

  return (
    <AuthContext.Provider value={{ user, token, login, logout, loading }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
